import { BRAND } from "./organization-palettes.ts";
import { useUiStore } from "../store/ui.ts";

interface Hsl {
  h: number;
  s: number;
  l: number;
}

function hexToHslParts(hex: string): Hsl {
  const [r, g, b] = [1, 3, 5].map(
    (i) => parseInt(hex.slice(i, i + 2), 16) / 255,
  );
  const max = Math.max(r!, g!, b!),
    min = Math.min(r!, g!, b!);
  let h = 0,
    s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g! - b!) / d + (g! < b! ? 6 : 0);
    else if (max === g) h = (b! - r!) / d + 2;
    else h = (r! - g!) / d + 4;
    h /= 6;
  }
  return { h: h * 360, s: s * 100, l: l * 100 };
}

const token = ({ h, s, l }: Hsl) =>
  `${Math.round(h)} ${Math.round(s)}% ${Math.round(l)}%`;

export function hexToHsl(hex: string): string {
  return token(hexToHslParts(hex));
}

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

/**
 * Las cinco series de los gráficos, derivadas del color de la clínica.
 *
 * Antes sólo la primera serie seguía a la paleta y el resto eran el azul y el
 * verde del sistema de diseño: una clínica magenta tenía las barras de egresos
 * azules, y el gráfico parecía prestado de otra aplicación.
 *
 * La escala se mueve sobre todo en luminosidad y apenas en tono —a lo sumo
 * dieciocho grados—, así que las series siguen siendo el mismo color pero se
 * distinguen entre sí. El tono puro no alcanzaba: dos líneas finas separadas
 * sólo por luminosidad se confunden en un gráfico de actividad.
 *
 * Los topes existen para los extremos del catálogo. Grafito casi no tiene
 * saturación y terracota es oscuro; sin límites, una escala construida a
 * ciegas termina con series invisibles contra el fondo o de un flúor que
 * ninguna clínica eligió.
 */
const RAMP = [
  { dh: 0, ds: 0, dl: 0 }, // el color elegido, sin tocar
  { dh: 14, ds: -10, dl: 26 },
  { dh: -18, ds: -22, dl: 44 },
  { dh: 6, ds: 8, dl: -14 },
  { dh: -12, ds: -6, dl: 34 },
];

/**
 * La ventana de luminosidad en la que una serie se ve.
 *
 * Cambia con el tema y no por gusto: contra un fondo casi blanco lo que
 * desaparece es lo claro, y contra uno casi negro, lo oscuro. La misma escala
 * usada en los dos lados deja siempre un par de series comidas por el fondo.
 */
const WINDOW = {
  light: { min: 24, max: 76 },
  dark: { min: 40, max: 84 },
};

/** Separación mínima entre dos series que el gráfico muestra juntas. */
const MIN_GAP = 12;

export type Theme = "light" | "dark";

/**
 * El color de la clínica, ajustado al tema.
 *
 * En claro se respeta tal cual: es la marca, y el catálogo entero está elegido
 * para leerse sobre blanco. En oscuro hay que aclararlo — teal #0F766E tiene
 * un 26% de luminosidad y sobre un fondo del 8% es casi invisible—, y de paso
 * bajarle un punto de saturación, que es lo que hace el sistema de diseño con
 * su propio color.
 */
export function brandColor(hex: string, theme: Theme): string {
  return token(themed(hexToHslParts(hex), theme));
}

function themed(base: Hsl, theme: Theme): Hsl {
  if (theme === "light") return base;
  return {
    h: base.h,
    s: clamp(base.s - 7, 10, 85),
    l: clamp(base.l + 16, 44, 64),
  };
}

export function chartRamp(hex: string, theme: Theme): string[] {
  const base = themed(hexToHslParts(hex), theme);
  const { min, max } = WINDOW[theme];
  return RAMP.map(({ dh, ds, dl }, index) => {
    if (index === 0) return token(base);
    // Si aclarar se pasa del techo, la serie oscurece en vez de aclarar. Sin
    // esta vuelta, una paleta que ya arranca clara —azul, violeta— manda tres
    // series contra el mismo tope y las deja indistinguibles entre sí: el
    // recorte se comía justo la separación que la escala venía a dar.
    //
    // Y si tampoco cabe hacia abajo, se toma el borde que quede más lejos en
    // vez de aplastarse contra el piso: un gris medio en tema oscuro —Pizarra—
    // no llegaba al techo por 1 punto, bajaba, y el piso de la ventana lo
    // devolvía a 11 puntos del base. Dos series a 11 puntos son dos series que
    // se confunden en el gráfico.
    const lighter = base.l + dl;
    const darker = base.l - Math.max(Math.abs(dl) * 0.8, MIN_GAP);
    const l =
      lighter <= max
        ? lighter
        : darker >= min
          ? darker
          : max - base.l >= base.l - min
            ? max
            : min;
    return token({
      h: (base.h + dh + 360) % 360,
      s: clamp(base.s + ds, 20, 92),
      l: clamp(l, min, max),
    });
  });
}

/**
 * El acento, que es una superficie con texto encima, y el color de ese texto.
 *
 * El pastel del catálogo sirve en claro y es exactamente lo que no sirve en
 * oscuro: una tarjeta menta al 90% de luminosidad en medio de una pantalla
 * negra es un agujero de luz. En oscuro se conserva el tono y se lleva la
 * superficie abajo, con el texto claro encima — el mismo reparto que el
 * sistema de diseño hace con su acento propio.
 */
export function accentTokens(
  hex: string,
  theme: Theme,
): { accent: string; foreground: string } {
  const base = hexToHslParts(hex);
  if (theme === "dark") {
    return {
      accent: token({ h: base.h, s: clamp(base.s * 0.55, 20, 50), l: 20 }),
      foreground: token({ h: base.h, s: clamp(base.s, 30, 70), l: 80 }),
    };
  }
  // En claro decide la luminancia real y no la luminosidad HSL: un pastel
  // amarillo y uno azul con el mismo 90% de luminosidad no brillan igual, y
  // el que decide si el texto va oscuro o claro es el ojo.
  const rgb = [1, 3, 5].map((index) =>
    parseInt(hex.slice(index, index + 2), 16),
  );
  const luminance =
    (0.2126 * rgb[0]! + 0.7152 * rgb[1]! + 0.0722 * rgb[2]!) / 255;
  return {
    accent: token(base),
    foreground: luminance > 0.55 ? "222 47% 11%" : "0 0% 100%",
  };
}

/**
 * Los tokens que pinta una organización, resueltos para un tema.
 *
 * Se escriben como estilo en línea sobre `<html>`, así que le ganan tanto a
 * `:root` como a `.dark`: el sistema de diseño ya no puede corregir nada por
 * debajo. Por eso el tema entra acá como argumento en vez de resolverse en
 * CSS — quien pisa las dos versiones se hace cargo de las dos.
 */
export function paletteTokens(
  primaryColor: string,
  accentColor: string,
  theme: Theme,
): Record<string, string> {
  const brand = brandColor(primaryColor, theme);
  const accent = accentTokens(accentColor, theme);
  return {
    primary: brand,
    ring: brand,
    // Las cinco series salen del mismo color, así que un gráfico se lee como
    // parte de la clínica y no como algo pegado encima. El acento no participa:
    // es pastel a propósito —lleva texto encima— y como serie desaparecía
    // contra el fondo.
    ...Object.fromEntries(
      chartRamp(primaryColor, theme).map((value, index) => [
        `chart-${index + 1}`,
        value,
      ]),
    ),
    accent: accent.accent,
    "accent-foreground": accent.foreground,
  };
}

/**
 * Los colores de la marca, escritos sobre `<html>`.
 *
 * La marca es del producto y no de cada clínica: se aplican los colores fijos
 * y se ignoran los que la organización tenga guardados. Las columnas siguen en
 * la base —borrarlas es una migración destructiva y no hace falta para esto—
 * pero ya no pintan nada.
 *
 * Por eso tampoco depende de que haya una organización cargada. Cuando dependía,
 * el ingreso y el reinicio de contraseña —que se dibujan fuera del shell, sin
 * sesión— se quedaban con el teal que el sistema de diseño trae por defecto:
 * la marca aparecía recién después de autenticarse.
 */
export function applyBrandTheme(
  // Claro por defecto: es el único tema del producto. El parámetro queda porque
  // la conversión de cada color a token sí distingue los dos, y las pruebas
  // recorren ambos para comprobar que ninguna combinación pierde contraste.
  theme: Theme = "light",
) {
  const root = document.documentElement;
  const primary = brandColor(BRAND.primary, theme);
  const tokens = {
    primary,
    ring: primary,
    accent: theme === "light" ? "130 9% 86%" : "130 9% 18%",
    "accent-foreground": theme === "light" ? "186 39% 15%" : "130 12% 90%",
    "chart-1": primary,
    "chart-2": theme === "light" ? "27 28% 52%" : "27 24% 62%",
    "chart-3": theme === "light" ? "151 37% 33%" : "151 32% 52%",
    "chart-4": theme === "light" ? "160 4% 40%" : "160 5% 62%",
    "chart-5": theme === "light" ? "6 53% 50%" : "6 48% 60%",
    // El tomate entero, para rellenos: no pasa por la escala de gráficos
    // porque no es una serie, es la marca.
    tomato: brandColor(BRAND.tomato, theme),
    // El lavanda pleno, sin adaptar al tema. `--accent` en oscuro es una
    // superficie oscura —lleva texto claro encima— y como luz no se vería;
    // esto es el pastel tal cual, que es lo que ilumina sobre un fondo negro.
    lavender: theme === "light" ? "27 28% 52%" : "27 24% 62%",
  };
  for (const [name, value] of Object.entries(tokens)) {
    root.style.setProperty(`--${name}`, value);
  }
  // Los gráficos ya resolvieron estos tokens a colores concretos antes de que
  // llegáramos acá; hay que decirles que vuelvan a leerlos.
  useUiStore.getState().paletteApplied();
}
